import { useCallback, useEffect, useRef, useState } from 'react';

import { computeReconciliation } from '../components/pages/reconciliation/engine';
import { type AssignedEntityLevel } from '../interface/agent-assignment';
import { type IChildResultDetail } from '../interface/child-agent';
import { type IReconciliationResult, type IReconIRevCoverage, type ReconLevel } from '../interface/reconciliation';
import { type IResultAnalyticsData } from '../interface/state/IElectionState';
import { getAssignmentsByEntity } from '../lib/agent-assignment-api';
import { getChildResultById } from '../lib/child-result-api';
import { getReconciliationDecision, getServerReconciliation, RECON_SERVER_ENABLED } from '../lib/reconciliation-api';
import { getResultAnalyticsByScope } from '../lib/result-analytics-api';

// Parent level → how to read its own result and enumerate/sum its DIRECT children.
// Reconciliation is per-rung: a State sums its LGAs, an LGA its wards, a ward its
// polling units — never the leaf PUs directly for higher levels.
const PARENT_MAP: Record<
	ReconLevel,
	{ own: AssignedEntityLevel; childResultType: string; childLevel: AssignedEntityLevel; scopeKey: 'stateId' | 'lgaId' | 'wardId' }
> = {
	EC8B: { own: 'Ward', childResultType: 'ec8a', childLevel: 'PollingUnit', scopeKey: 'wardId' },
	EC8C: { own: 'LGA', childResultType: 'ec8b', childLevel: 'Ward', scopeKey: 'lgaId' },
	EC8D: { own: 'State', childResultType: 'ec8c', childLevel: 'LGA', scopeKey: 'stateId' },
};

// IReV lives only at polling-unit (EC8A) level. To roll it up for a parent we
// enumerate ALL descendant PUs with an IReV row, scoped by the parent's own id.
const PU_SCOPE_KEY: Record<ReconLevel, 'stateId' | 'lgaId' | 'wardId'> = {
	EC8B: 'wardId',
	EC8C: 'lgaId',
	EC8D: 'stateId',
};

// Cap on IReV PU detail fetches to avoid a request storm on a large State subtree.
// Beyond this we report the available count but roll up only the first N (the
// coverage caption makes the partial nature explicit).
const IREV_FETCH_CAP = 600;

export interface IReconTarget {
	electionId: number;
	// Parent level that owns the reconciliation.
	level: ReconLevel;
	// The parent's own entity code + numeric id (the id scopes the child query).
	entityCode: string;
	entityId: number;
	entityName?: string;
}

export type ReconLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

// One direct child of a reconciliation target, with its own drill target (null
// for a leaf polling unit) and its uploaded result detail (null until uploaded).
export interface IReconChild {
	code: string;
	resultType: string;
	isUploaded: boolean;
	detail: IChildResultDetail | null;
	target: IReconTarget | null;
}

/**
 * Treat an analytics row as a reconciliation subject: its own code + the id that
 * scopes ITS children. Returns null for a leaf polling unit (EC8A), which has no
 * children to reconcile. Used to build the recursive drill-down tree.
 */
export const rowToReconTarget = (row: IResultAnalyticsData, electionId: number): IReconTarget | null => {
	const level = row.resultType?.toUpperCase() as ReconLevel;
	const map = PARENT_MAP[level];
	if (!map) return null;
	return { electionId, level, entityCode: row.code, entityId: row[map.scopeKey], entityName: row.code };
};

const ok = (status: number) => status >= 200 && status < 300;

const detailOrNull = async (level: AssignedEntityLevel, resultId?: string | null): Promise<IChildResultDetail | null> => {
	if (!resultId) return null;
	const outcome = await getChildResultById(level, resultId);
	return outcome.reachable && ok(outcome.httpStatus) ? outcome.body : null;
};

/**
 * Roll up the IReV tiebreaker for a parent: enumerate its descendant polling
 * units that carry an IReV row (ResultSource=IRev), fetch each, and report
 * coverage. Returns nulls when no IReV data exists → the engine/UI degrade to
 * "IReV unavailable". Bounded by IREV_FETCH_CAP so a large subtree can't storm.
 */
const fetchIRev = async (
	target: IReconTarget
): Promise<{ details: (IChildResultDetail | null)[]; coverage: IReconIRevCoverage | null }> => {
	const analytics = await getResultAnalyticsByScope({
		electionId: target.electionId,
		resultType: 'ec8a',
		[PU_SCOPE_KEY[target.level]]: target.entityId,
		resultSource: 'IRev',
	});
	const rows = analytics.reachable && analytics.body?.data ? analytics.body.data : [];
	const stats = analytics.reachable ? analytics.body?.statistics : undefined;
	const uploaded = rows.filter((r) => r.isUploaded && r.resultId);
	if (!uploaded.length) {
		return { details: [], coverage: { irevTotal: stats?.total ?? 0, irevAvailable: 0 } };
	}
	const toFetch = uploaded.slice(0, IREV_FETCH_CAP);
	const details = await Promise.all(toFetch.map((r) => detailOrNull('PollingUnit', r.resultId)));
	return { details, coverage: { irevTotal: stats?.total ?? uploaded.length, irevAvailable: toFetch.length } };
};

export interface IReconOptions {
	// Roll up the IReV tiebreaker column. Off by default — it fans out a fetch per
	// descendant polling unit, so only the full page opts in (not the overview card
	// or the drawer strip).
	includeIRev?: boolean;
}

/**
 * Load a parent-vs-children reconciliation for one entity. Computes the
 * comparison on the client from existing per-level endpoints (Source A = the
 * parent's own upload, Source B = the sum of its direct children); flips to the
 * server endpoint transparently once RECON_SERVER_ENABLED is set. Pass
 * `includeIRev` to also roll up the IReV tiebreaker (degrades to "unavailable"
 * when no IReV data exists for the subtree).
 */
export const useReconciliation = (target?: IReconTarget | null, options?: IReconOptions) => {
	const [status, setStatus] = useState<ReconLoadStatus>('idle');
	const [result, setResult] = useState<IReconciliationResult | null>(null);
	const [children, setChildren] = useState<IReconChild[]>([]);
	const runId = useRef(0);

	const includeIRev = !!options?.includeIRev;
	const key = target ? `${target.electionId}:${target.level}:${target.entityCode}:${target.entityId}:${includeIRev}` : '';

	const load = useCallback(async () => {
		if (!target?.entityCode || !target.entityId || !target.electionId) {
			setStatus('idle');
			setResult(null);
			return;
		}
		const map = PARENT_MAP[target.level];
		if (!map) {
			setStatus('idle');
			return;
		}

		runId.current += 1;
		const myRun = runId.current;
		setStatus('loading');

		try {
			// 1. Source A resultId — the parent's own upload, via its assignment row.
			const assignment = await getAssignmentsByEntity([target.entityCode], target.electionId);
			const items = assignment.reachable ? assignment.body?.data?.items : undefined;
			const sourceAResultId = items?.[0]?.assignmentMeta?.resultId ?? null;

			// 2. Source A detail.
			const sourceADetail = await detailOrNull(map.own, sourceAResultId);

			// 3. Direct children + completeness (roster total vs uploaded count).
			const analytics = await getResultAnalyticsByScope({
				electionId: target.electionId,
				resultType: map.childResultType,
				[map.scopeKey]: target.entityId,
			});
			const rows = analytics.reachable && analytics.body?.data ? analytics.body.data : [];
			const stats = analytics.reachable ? analytics.body?.statistics : undefined;
			const childrenTotal = stats?.total ?? rows.length;
			const childrenUploaded = stats?.uploadedCount ?? rows.filter((r) => r.isUploaded).length;

			// Read-back the persisted decision + (optionally) the IReV roll-up in
			// parallel with the child fetches. When the server path is on it computes
			// IReV itself (IncludeIRev), so we skip the client roll-up to avoid a
			// per-PU fetch storm — the client roll-up only matters as a fallback.
			const decisionPromise = getReconciliationDecision(target.electionId, target.level, target.entityCode);
			const irevPromise = includeIRev && !RECON_SERVER_ENABLED ? fetchIRev(target) : null;

			// 4. Sum the uploaded children into Source B, aligning each row with its
			// detail for the drill-down tree (needed for both server and client paths).
			const uploadedRows = rows.filter((r) => r.isUploaded && r.resultId);
			const uploadedDetails = await Promise.all(uploadedRows.map((r) => detailOrNull(map.childLevel, r.resultId)));

			if (myRun !== runId.current) return;

			const decisionOutcome = await decisionPromise;
			// Only accept a well-formed decision object. The GET route can collide with
			// the generic Result/{id} route on a backend without the endpoint deployed,
			// returning a string/other shape — which must never reach the UI (a string
			// `decision` would turn `decision?.at`/`.note` property reads into String
			// prototype methods and crash a useState initializer).
			const rawDecision = decisionOutcome.reachable && ok(decisionOutcome.httpStatus) ? decisionOutcome.body : null;
			const decision =
				rawDecision && typeof rawDecision === 'object' && typeof rawDecision.state === 'string' ? rawDecision : null;
			const irev = irevPromise ? await irevPromise : null;

			const detailByResultId = new Map<string, IChildResultDetail | null>();
			uploadedRows.forEach((r, i) => detailByResultId.set(r.resultId, uploadedDetails[i]));
			const childList: IReconChild[] = rows.map((r) => ({
				code: r.code,
				resultType: r.resultType,
				isUploaded: r.isUploaded,
				detail: r.isUploaded ? (detailByResultId.get(r.resultId) ?? null) : null,
				target: rowToReconTarget(r, target.electionId),
			}));

			// 5. Server path (once deployed) — identical contract, no client math.
			// It needs childrenTotal (the roster the read store lacks) to gate the
			// verdict, so we compute it above first. The tree still uses childList.
			if (RECON_SERVER_ENABLED) {
				const server = await getServerReconciliation({
					electionId: target.electionId,
					level: target.level,
					entityCode: target.entityCode,
					childrenTotal,
					includeIRev,
				});
				if (myRun !== runId.current) return;
				if (server.reachable && ok(server.httpStatus) && server.body) {
					setChildren(childList);
					setResult({ ...server.body, decision: server.body.decision ?? decision });
					setStatus('ready');
					return;
				}
				// fall through to client compute on any server miss
			}

			// 6. Client compute — including the IReV roll-up when requested.
			const computed = computeReconciliation({
				level: target.level,
				entityCode: target.entityCode,
				entityName: target.entityName,
				sourceADetail,
				childDetails: uploadedDetails,
				completeness: { childrenTotal, childrenUploaded },
				irevDetails: irev?.details,
				irevCoverage: irev?.coverage,
			});

			setChildren(childList);
			setResult({ ...computed, decision });
			setStatus('ready');
		} catch {
			if (myRun !== runId.current) return;
			setStatus('error');
		}
	}, [key]);

	useEffect(() => {
		load();
	}, [load]);

	const isLocked = result?.verdict === 'awaitingCoverage';
	const unitsRemaining = result ? Math.max(result.completeness.childrenTotal - result.completeness.childrenUploaded, 0) : 0;

	return { status, result, children, isLocked, unitsRemaining, refresh: load };
};
